import React, { useMemo } from 'react';
import { Card } from 'antd';
import ClipLoader from 'react-spinners/ClipLoader';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { ProductClickLocationData, PURCHASE_SOURCE_NAME_MAP } from '@models/statistics';

// Chart.js 등록
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface ProductClickLocationChartProps {
  data: ProductClickLocationData[];
  isLoading: boolean;
}

export const ProductClickLocationChart: React.FC<ProductClickLocationChartProps> = ({ data, isLoading }) => {
  // 차트 데이터 생성
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return { labels: [], datasets: [] };
    }

    // 모든 위치 ID 수집 (중복 제거)
    const allLocationIds = Array.from(
      new Set(data.flatMap((dayData) => dayData.locations.map((loc) => loc.locationId))),
    ).sort();

    // 날짜 라벨 생성
    const dateLabels = data.map((dayData) => {
      const date = new Date(dayData._id);
      return `${date.getMonth() + 1}/${date.getDate()}`;
    });

    // 각 위치별 데이터셋 생성 (Line Chart용)
    const datasets = allLocationIds
      .map((locationId, index) => {
        const color = `hsl(${(index * 360) / allLocationIds.length}, 70%, 50%)`;

        const dataPoints = data.map((dayData) => {
          const locationData = dayData.locations.find((loc) => loc.locationId === locationId);
          return locationData ? locationData.count : 0;
        });

        return {
          label: PURCHASE_SOURCE_NAME_MAP[locationId as keyof typeof PURCHASE_SOURCE_NAME_MAP] || locationId,
          data: dataPoints,
          borderColor: color,
          backgroundColor: color + '20', // 투명도 12%
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: color,
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          tension: 0.1, // 부드러운 곡선
          fill: false, // 영역 채우기 비활성화
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    return {
      labels: dateLabels,
      datasets,
    };
  }, [data]);

  // Chart.js 옵션 (Line Chart용)
  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 12,
          font: {
            size: 11,
          },
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          beforeBody: function (tooltipItems) {
            // 해당 날짜의 모든 위치별 데이터를 수집하고 카운트 순으로 정렬
            const dataAtIndex = tooltipItems.map((item) => ({
              locationId: item.dataset.label,
              count: item.parsed.y,
            }));

            // 카운트가 높은 순으로 정렬 (count가 0인 것들은 제외)
            const sortedData = dataAtIndex.filter((item) => item.count > 0).sort((a, b) => b.count - a.count);

            if (sortedData.length === 0) {
              return ['해당 날짜에 클릭 데이터가 없습니다.'];
            }

            return ['카운트 순 정렬:', ...sortedData.map((item) => `${item.locationId}: ${item.count}건`)];
          },
          label: function () {
            // beforeBody에서 이미 모든 정보를 표시하므로 개별 label은 비활성화
            return '';
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: '날짜',
        },
        grid: {
          display: true,
          color: '#f0f0f0',
        },
      },
      y: {
        title: {
          display: true,
          text: '클릭 수',
        },
        beginAtZero: true,
        grid: {
          display: true,
          color: '#f0f0f0',
        },
        ticks: {
          precision: 0,
        },
      },
    },
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    hover: {
      mode: 'index',
      intersect: false,
    },
    elements: {
      point: {
        hoverRadius: 8,
      },
    },
  };

  if (isLoading) {
    return (
      <Card className="chart" title="일별 위치별 상품 클릭 수" bordered={false}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <ClipLoader />
        </div>
      </Card>
    );
  }

  return (
    <Card
      className="chart"
      title="일별 위치별 상품 클릭 수"
      bordered={false}
      style={{ minWidth: 'calc(50% - 6px)', maxWidth: '840px' }}>
      <div style={{ height: '400px', padding: '20px 0' }}>
        {data && data.length > 0 ? (
          <Line data={chartData} options={chartOptions} />
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: '#999',
            }}>
            해당 기간에 상품 클릭 데이터가 없습니다.
          </div>
        )}
      </div>
    </Card>
  );
};

export default ProductClickLocationChart;
